import { addDays, addYears, format, isValid, parseISO } from 'date-fns';
import {
	DIRECTOR_COMPLIANCE_CHECKLIST,
	DIRECTOR_COMPLIANCE_CHECKLIST_KEY,
} from '@/utils/directorComplianceChecklist';

/*
 * Company records are kept as yearly versions. The newest version of a record is the current one;
 * older versions are its archive and stay as they were reviewed.
 */

export const COMPANY_RECORD_TEMPLATES = Object.freeze({
	[DIRECTOR_COMPLIANCE_CHECKLIST_KEY]: DIRECTOR_COMPLIANCE_CHECKLIST,
});

export const COMPANY_RECORD_TEMPLATE_KEYS = Object.freeze(Object.keys(COMPANY_RECORD_TEMPLATES));

export const getCompanyRecordTemplate = (templateKey) => COMPANY_RECORD_TEMPLATES[templateKey] || null;

// An item counts as coming up this long before its next due date.
export const DUE_SOON_DAYS = 30;

const ENTRY_FIELDS = ['nextDue', 'done', 'notes'];

const text = (value) => (value === null || value === undefined ? '' : String(value));

const parseDate = (value) => {
	if (!value) return null;
	const parsed = parseISO(String(value));
	return isValid(parsed) ? parsed : null;
};

const isoDate = (date) => format(date, 'yyyy-MM-dd');

export const todayIso = () => isoDate(new Date());

const firstOfMonth = (date) => isoDate(new Date(date.getFullYear(), date.getMonth(), 1));

export const formatRecordDate = (value) => {
	const date = parseDate(value);
	return date ? format(date, 'dd/MM/yyyy') : '';
};

export const formatRecordMonth = (value) => {
	const date = parseDate(value);
	return date ? format(date, 'MMMM yyyy') : '';
};

export const formatRecordVersion = (version) => `V${Number(version) || 1}`;

const cloneSections = (sections = []) =>
	sections.map((section) => ({
		key: text(section.key),
		title: text(section.title),
		...(section.intro ? { intro: text(section.intro) } : {}),
		rows: (Array.isArray(section.rows) ? section.rows : []).map((row) => ({
			key: text(row.key),
			item: text(row.item),
			check: text(row.check),
			frequency: text(row.frequency),
			who: text(row.who),
		})),
	}));

const normalizeEntry = (entry = {}) =>
	Object.fromEntries(ENTRY_FIELDS.map((field) => [field, text(entry?.[field])]));

const rowKeysOf = (sections) => sections.flatMap((section) => section.rows.map((row) => row.key));

/**
 * The record's content in a complete shape. Each version keeps its own copy of the checklist text,
 * so an archived version reads as it was even after the checklist is reworded.
 */
export const normalizeRecordContent = (content = {}, templateKey = DIRECTOR_COMPLIANCE_CHECKLIST_KEY) => {
	const template = getCompanyRecordTemplate(templateKey) || DIRECTOR_COMPLIANCE_CHECKLIST;
	const sections = cloneSections(
		Array.isArray(content?.sections) && content.sections.length ? content.sections : template.sections,
	);
	const sourceEntries = content?.entries && typeof content.entries === 'object' ? content.entries : {};
	return {
		templateKey,
		intro: Array.isArray(content?.intro) ? content.intro.map(text) : [...template.intro],
		sections,
		entries: Object.fromEntries(rowKeysOf(sections).map((key) => [key, normalizeEntry(sourceEntries[key])])),
		signOff: {
			completedBy: text(content?.signOff?.completedBy),
			date: text(content?.signOff?.date),
		},
	};
};

/** Version 1 of a record, filled in with what the director had already written on paper. */
export const createFirstVersion = (templateKey) => {
	const template = getCompanyRecordTemplate(templateKey);
	if (!template) throw new Error(`Unknown company record: ${templateKey}`);
	const first = template.firstVersion || {};
	const today = new Date();
	const reviewedOn = first.reviewedOn || firstOfMonth(today);
	return {
		templateKey,
		title: template.title,
		reference: template.reference,
		version: 1,
		reviewedOn,
		nextReviewOn: first.nextReviewOn || isoDate(addYears(parseDate(reviewedOn), 1)),
		content: normalizeRecordContent({ entries: first.entries || {} }, templateKey),
	};
};

/**
 * The next yearly version. The checklist and each item's next due date and notes carry over;
 * the dates items were done and the sign-off start empty.
 */
export const createNextVersion = (record, { today = new Date() } = {}) => {
	const template = getCompanyRecordTemplate(record?.templateKey);
	if (!template) throw new Error(`Unknown company record: ${record?.templateKey}`);
	const previous = normalizeRecordContent(record.content, record.templateKey);
	const reviewedOn = firstOfMonth(today);
	const entries = Object.fromEntries(
		Object.entries(previous.entries).map(([key, entry]) => [
			key,
			{ nextDue: entry.nextDue, done: '', notes: entry.notes },
		]),
	);
	return {
		templateKey: record.templateKey,
		title: template.title,
		reference: template.reference,
		version: (Number(record.version) || 1) + 1,
		reviewedOn,
		nextReviewOn: isoDate(addYears(parseDate(reviewedOn), 1)),
		// The new version takes the checklist as it is written now.
		content: normalizeRecordContent(
			{ intro: template.intro, sections: template.sections, entries },
			record.templateKey,
		),
	};
};

/** What starting the next yearly review does, as its confirmation explains it. */
export const nextReviewDialog = (record, title) => {
	const current = formatRecordVersion(record?.version);
	const next = formatRecordVersion((Number(record?.version) || 1) + 1);
	return {
		title: `Start ${next} of ${title}`,
		lines: [
			`${next} starts from ${current}: next due dates and notes carry over; done dates and the sign-off start empty.`,
			`${current} moves to the archive. It can still be opened and downloaded, but no longer changed.`,
		],
		actionLabel: `Start ${next}`,
		busyLabel: 'Starting...',
		tone: 'primary',
	};
};

/**
 * Where an item stands: 'done' once it was checked for its due date (or up to 30 days before),
 * otherwise 'overdue', 'soon' (within 30 days) or 'scheduled'. Items without a due date are 'none'.
 */
export const entryDueState = (entry = {}, today = todayIso()) => {
	const due = parseDate(entry.nextDue);
	if (!due) return 'none';
	const done = parseDate(entry.done);
	if (done && done >= addDays(due, -DUE_SOON_DAYS)) return 'done';
	const now = parseDate(today) || new Date();
	if (due < now) return 'overdue';
	if (due <= addDays(now, DUE_SOON_DAYS)) return 'soon';
	return 'scheduled';
};

export const summarizeRecord = (record, today = todayIso()) => {
	const content = normalizeRecordContent(record?.content, record?.templateKey);
	const states = Object.values(content.entries).map((entry) => entryDueState(entry, today));
	const nextReview = parseDate(record?.nextReviewOn);
	return {
		overdue: states.filter((state) => state === 'overdue').length,
		soon: states.filter((state) => state === 'soon').length,
		reviewDue: Boolean(nextReview && nextReview <= (parseDate(today) || new Date())),
	};
};

const subjectKey = (record) => text(record?.subject?.documentId || record?.subject?.id || '');

/**
 * Groups versions into records. Each group lists its versions newest first; the first one is
 * the current version.
 */
export const groupRecordVersions = (records = []) => {
	const groups = new Map();
	records.forEach((record) => {
		const key = `${record.templateKey}::${subjectKey(record)}`;
		if (!groups.has(key)) groups.set(key, []);
		groups.get(key).push(record);
	});
	return Array.from(groups.entries()).map(([key, versions]) => ({
		key,
		templateKey: versions[0].templateKey,
		versions: [...versions].sort((a, b) => (Number(b.version) || 0) - (Number(a.version) || 0)),
	}));
};

export const isCurrentVersion = (record, records = []) =>
	!records.some(
		(other) =>
			other.templateKey === record.templateKey &&
			subjectKey(other) === subjectKey(record) &&
			(Number(other.version) || 0) > (Number(record.version) || 0),
	);

const canonical = (value) => {
	if (Array.isArray(value)) return value.map(canonical);
	if (value && typeof value === 'object') {
		return Object.keys(value)
			.sort()
			.reduce((result, key) => {
				if (value[key] !== undefined) result[key] = canonical(value[key]);
				return result;
			}, {});
	}
	return value;
};

export const sameContent = (a, b) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isIdList = (value) => Array.isArray(value) && value.every((item) => isPlainObject(item) && item.id);

/**
 * Three-way merge of a record edited on two devices: what this device changed since it last read
 * the record is laid over what is saved now. Rows of a list are matched by id, so rows
 * added on both devices are all kept. Where both changed the same cell, this device's value wins.
 */
export const mergeRecordContent = (base, mine, theirs) => {
	if (sameContent(mine, base)) return theirs;
	if (sameContent(theirs, base) || sameContent(mine, theirs)) return mine;
	if (isPlainObject(mine) && isPlainObject(theirs)) {
		const baseObject = isPlainObject(base) ? base : {};
		const merged = {};
		new Set([...Object.keys(theirs), ...Object.keys(mine)]).forEach((key) => {
			const value = mergeRecordContent(baseObject[key], mine[key], theirs[key]);
			if (value !== undefined) merged[key] = value;
		});
		return merged;
	}
	if (isIdList(mine) && isIdList(theirs)) {
		const baseById = new Map((isIdList(base) ? base : []).map((item) => [item.id, item]));
		const mineById = new Map(mine.map((item) => [item.id, item]));
		const theirsById = new Map(theirs.map((item) => [item.id, item]));
		const merged = [];
		theirs.forEach((item) => {
			const own = mineById.get(item.id);
			const original = baseById.get(item.id);
			// Removed here: it stays only if the other device changed it meanwhile.
			if (!own && original) {
				if (!sameContent(item, original)) merged.push(item);
				return;
			}
			merged.push(own ? mergeRecordContent(original, own, item) : item);
		});
		mine.forEach((item) => {
			if (theirsById.has(item.id)) return;
			const original = baseById.get(item.id);
			// Removed on the other device: it stays only if it was changed here.
			if (original && sameContent(item, original)) return;
			merged.push(item);
		});
		return merged;
	}
	return mine;
};
